import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PortariaModule.css';

interface EntradaPortaria {
  id: number;
  tipo_entrada: string;
  data_chegada: string;
  placa_veiculo: string;
  placa_carreta?: string;
  nome_motorista: string;
  cpf_motoristas: string;
  local_estacionamento: string;
  tipo_operacao?: string;
  empresa_coletando?: string;
  notas_fiscais?: string;
  status: 'Entrada' | 'Saida';
  data_saida?: string;
}


const PortariaModule: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [entradas, setEntradas] = useState<EntradaPortaria[]>([]);
  const [locaisOcupados, setLocaisOcupados] = useState<string[]>([]);
  const [filtroTipo, setFiltroTipo] = useState<string[]>([
    'Visita', 
    'Prestador de Serviço', 
    'Motorista Terceiro', 
    'Funcionário', 
    'Motorista Frota'
  ]);
  const [form, setForm] = useState({
    tipo_entrada: '',
    data_chegada: '',
    placa_veiculo: '',
    placa_carreta: '',
    nome_motorista: '',
    cpf_motoristas: '',
    local_estacionamento: '',
    tipo_operacao: '',
    empresa_coletando: '',
    notas_fiscais: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEntradas();
    fetchLocaisOcupados();
  }, []);

  const fetchEntradas = async () => {
    try {
      const response = await axios.get('/api/portaria/entradas');
      if (response.data.success) {
        setEntradas(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar entradas:', error);
    }
  };

  const fetchLocaisOcupados = async () => {
    try {
      const response = await axios.get('/api/portaria/locais-ocupados');
      if (response.data.success) {
        setLocaisOcupados(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar locais ocupados:', error);
    }
  };

  const handleFiltroChange = (tipo: string) => {
    if (filtroTipo.includes(tipo)) {
      setFiltroTipo(filtroTipo.filter(t => t !== tipo));
    } else {
      setFiltroTipo([...filtroTipo, tipo]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.post('/api/portaria/entradas', form);
      if (response.data.success) {
        alert('Entrada registrada com sucesso!');
        setShowModal(false);
        setForm({
          tipo_entrada: '',
          data_chegada: '',
          placa_veiculo: '',
          placa_carreta: '',
          nome_motorista: '',
          cpf_motoristas: '',
          local_estacionamento: '',
          tipo_operacao: '',
          empresa_coletando: '',
          notas_fiscais: ''
        });
        fetchEntradas();
        fetchLocaisOcupados();
      }
    } catch (error: any) {
      console.error('Erro ao registrar entrada:', error);
      alert(error.response?.data?.message || 'Erro ao registrar entrada');
    } finally {
      setLoading(false);
    }
  };

  const handleSaida = async (id: number) => {
    const confirmar = window.confirm('Deseja registrar a saída deste veículo?');
    if (!confirmar) return;

    try {
      const response = await axios.patch(`/api/portaria/entradas/${id}/saida`);
      
      if (response.data.success) {
        alert('Saída registrada com sucesso!');
        fetchEntradas();
        fetchLocaisOcupados();
      }
    } catch (error: any) {
      console.error('Erro ao registrar saída:', error);
      alert(error.response?.data?.message || 'Erro ao registrar saída');
    }
  };

  const calcularTempoParado = (dataChegada: string) => {
    const agora = new Date();
    const chegada = new Date(dataChegada);
    const diffMs = agora.getTime() - chegada.getTime();
    
    const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (dias > 0) {
      return `${dias}d ${horas}h ${minutos}m`;
    } else if (horas > 0) {
      return `${horas}h ${minutos}m`;
    } else {
      return `${minutos}m`;
    }
  };

  const formatarData = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR') + ', ' + data.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const entradasFiltradas = entradas.filter(entrada => 
    filtroTipo.includes(entrada.tipo_entrada)
  );

  return (
    <div className="portaria-module">
      <div className="patio-header">
        <h2>Controle de Portaria</h2>
        <div className="header-actions">
          <button className="btn-exportar" disabled>
            📊 Exportar Relatório
          </button>
          <button className="btn-nova-entrada" onClick={() => setShowModal(true)}>
            + Nova Entrada
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <h3>Entradas</h3>
        <div className="filtros-checkboxes">
          {["Visita", "Prestador de Serviço", "Motorista Terceiro", "Funcionário", "Motorista Frota"].map((tipo) => (
            <label key={tipo} className={`filtro-checkbox`}>
              <input
                type="checkbox"
                checked={filtroTipo.includes(tipo)}
                onChange={() => handleFiltroChange(tipo)}
              />
              <span>{tipo}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Cards das Entradas */}
      <div className="entradas-grid">
        {entradasFiltradas.map((entrada) => (
          <div key={entrada.id} className="entrada-card">
            <div className="card-header">
              <h4>Placa: {entrada.placa_veiculo}</h4>
              <span className="badge">{entrada.tipo_entrada}</span>
            </div>
            <div className="card-body">
              <p><strong>Motorista:</strong> {entrada.nome_motorista}</p>
              <p><strong>CPF Motorista(s):</strong> {entrada.cpf_motoristas}</p>
              {entrada.placa_carreta && <p><strong>Placa Carreta:</strong> {entrada.placa_carreta}</p>}
              <p><strong>Data de Chegada:</strong> {formatarData(entrada.data_chegada)}</p>
              <p><strong>Local:</strong> {entrada.local_estacionamento}</p>
              {entrada.tipo_operacao && <p><strong>Operação:</strong> {entrada.tipo_operacao}</p>}
              {entrada.empresa_coletando && <p><strong>Empresa Coletando:</strong> {entrada.empresa_coletando}</p>}
              {entrada.notas_fiscais && <p><strong>Notas Fiscais:</strong> {entrada.notas_fiscais}</p>}
              <p><strong>Tempo Parado:</strong> <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{calcularTempoParado(entrada.data_chegada)}</span></p>
            </div>
            <div className="card-footer">
              <button 
                className="btn-saida"
                onClick={() => handleSaida(entrada.id)}
              >
                SAÍDA
              </button>
            </div>
          </div>
        ))}
      </div>

      {entradasFiltradas.length === 0 && (
        <div className="empty-state">
          <p>Nenhum veículo na portaria no momento.</p>
        </div>
      )}

      {/* Modal de Nova Entrada */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Checklist de Entrada</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form className="entrada-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Tipo de entrada</label>
                <select name="tipo_entrada" value={form.tipo_entrada} onChange={handleInputChange} required>
                  <option value="">Selecione...</option>
                  <option value="Visita">Visita</option>
                  <option value="Prestador de Serviço">Prestador de Serviço</option>
                  <option value="Motorista Terceiro">Motorista Terceiro</option>
                  <option value="Funcionário">Funcionário</option>
                  <option value="Motorista Frota">Motorista Frota</option>
                </select>
              </div>

              <div className="form-group">
                <label>Data de chegada *</label>
                <input type="datetime-local" name="data_chegada" value={form.data_chegada} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Placa do veículo *</label>
                <input type="text" name="placa_veiculo" value={form.placa_veiculo} onChange={handleInputChange} placeholder="Digite a placa do veículo" required />
              </div>

              <div className="form-group">
                <label>Placa da carreta (opcional)</label>
                <input type="text" name="placa_carreta" value={form.placa_carreta} onChange={handleInputChange} placeholder="Digite a placa da carreta" />
              </div>

              <div className="form-group">
                <label>Nome do motorista *</label>
                <input type="text" name="nome_motorista" value={form.nome_motorista} onChange={handleInputChange} placeholder="Digite o nome do motorista" required />
              </div>

              <div className="form-group">
                <label>CPF dos motoristas *</label>
                <input type="text" name="cpf_motoristas" value={form.cpf_motoristas} onChange={handleInputChange} placeholder="Digite o CPF do motorista" required />
                <small>Se houver mais de um, separe por vírgula.</small>
              </div>

              <div className="form-group">
                <label>Local de Estacionamento *</label>
                <select name="local_estacionamento" value={form.local_estacionamento} onChange={handleInputChange} required>
                  <option value="">Escolha...</option>
                  <option value="Doca 1" disabled={locaisOcupados.includes('Doca 1')}>
                    Doca 1 {locaisOcupados.includes('Doca 1') && '(Ocupada)'}
                  </option>
                  <option value="Doca 2" disabled={locaisOcupados.includes('Doca 2')}>
                    Doca 2 {locaisOcupados.includes('Doca 2') && '(Ocupada)'}
                  </option>
                  <option value="Doca 3" disabled={locaisOcupados.includes('Doca 3')}>
                    Doca 3 {locaisOcupados.includes('Doca 3') && '(Ocupada)'}
                  </option>
                  <option value="Doca 4" disabled={locaisOcupados.includes('Doca 4')}>
                    Doca 4 {locaisOcupados.includes('Doca 4') && '(Ocupada)'}
                  </option>
                  <option value="Doca 5" disabled={locaisOcupados.includes('Doca 5')}>
                    Doca 5 {locaisOcupados.includes('Doca 5') && '(Ocupada)'}
                  </option>
                  <option value="Doca 6" disabled={locaisOcupados.includes('Doca 6')}>
                    Doca 6 {locaisOcupados.includes('Doca 6') && '(Ocupada)'}
                  </option>
                  <option value="Doca 7 Coberta" disabled={locaisOcupados.includes('Doca 7 Coberta')}>
                    Doca 7 Coberta {locaisOcupados.includes('Doca 7 Coberta') && '(Ocupada)'}
                  </option>
                  <option value="Pátio">Pátio</option>
                  <option value="Mecanica">Mecanica</option>
                  <option value="Administração">Administração</option>
                </select>
              </div>

              {/* Campo condicional para Motorista Terceiro e Motorista Frota */}
              {(form.tipo_entrada === 'Motorista Terceiro' || form.tipo_entrada === 'Motorista Frota') && (
                <div className="form-group">
                  <label>Tipo de Operação *</label>
                  <select name="tipo_operacao" value={form.tipo_operacao} onChange={handleInputChange} required>
                    <option value="">Selecione...</option>
                    <option value="Carga">Carga</option>
                    <option value="Descarga">Descarga</option>
                    <option value="Nenhum">Nenhum</option>
                  </select>
                </div>
              )}

              {/* Campo de empresa coletando - apenas se for Carga */}
              {form.tipo_operacao === 'Carga' && (
                <div className="form-group">
                  <label>Empresa Coletando *</label>
                  <input type="text" name="empresa_coletando" value={form.empresa_coletando} onChange={handleInputChange} placeholder="Nome da empresa" required />
                </div>
              )}

              {/* Campo de notas fiscais - apenas se for Descarga */}
              {form.tipo_operacao === 'Descarga' && (
                <div className="form-group">
                  <label>Número(s) da Nota Fiscal *</label>
                  <input type="text" name="notas_fiscais" value={form.notas_fiscais} onChange={handleInputChange} placeholder="Digite ou bipar os números das notas fiscais" required />
                  <small>Se houver mais de uma, separe por vírgula.</small>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancelar">
                  Cancelar
                </button>
                <button type="submit" className="btn-registrar" disabled={loading}>
                  {loading ? 'Salvando...' : 'REGISTRAR'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortariaModule;